import {Capacitor} from '@capacitor/core';
import {SessionRepository} from '../user/session-repository';

export class ApiRepository {
    constructor(private sessionRepository: SessionRepository) {}

    public async get<T>(path: string): Promise<T> {
        const headers: HeadersInit = {};
        const jwt = this.sessionRepository.getJwt();

        if (jwt !== undefined) {
            headers['session-token'] = jwt;
        }

        const response = await fetch(`${baseUrl}${path}`, {
            headers,
            method: 'GET',
        });

        if (response.ok) {
            return await response.json();
        } else if (response.status === 403) {
            const errorResponse = await response.json();

            throw new ForbiddenError(errorResponse.errorMessageCode);
        } else {
            throw new Error(response.statusText);
        }
    }

    public async getFile(path: string): Promise<Blob> {
        const headers: HeadersInit = {};
        const jwt = this.sessionRepository.getJwt();

        if (jwt !== undefined) {
            headers['session-token'] = jwt;
        }

        const response = await fetch(`${baseUrl}${path}`, {
            headers,
            method: 'GET',
        });

        if (response.ok) {
            return await response.blob();
        } else if (response.status === 403) {
            const errorResponse = await response.json();

            throw new ForbiddenError(errorResponse.errorMessageCode);
        } else {
            throw new Error(response.statusText);
        }
    }

    public async postFile<T>(path: string, file: File): Promise<T> {
        const headers: HeadersInit = {};

        const formData = new FormData();

        formData.append('file', file);
        const jwt = this.sessionRepository.getJwt();

        if (jwt !== undefined) {
            headers['session-token'] = jwt;
        }

        const response = await fetch(`${baseUrl}${path}`, {
            headers,
            method: 'POST',
            body: formData,
        });

        if (response.ok) {
            return await response.json();
        } else if (response.status === 400) {
            const errorResponse = await response.json();

            if (errorResponse.errorMessageCode) {
                throw new ValidationError(errorResponse.errorMessageCode);
            } else {
                throw new ValidationErrorMultiple(errorResponse.validationErrors.map((error: {errorMessage: string}) => error.errorMessage));
            }
        } else if (response.status === 403) {
            const errorResponse = await response.json();

            throw new ForbiddenError(errorResponse.errorMessageCode);
        } else {
            throw new Error(response.statusText);
        }
    }

    public async post<T>(path: string, body: unknown = {}, additionalHeaders: Record<string,string> = {}): Promise<T> {
        const headers: HeadersInit = {
            ...additionalHeaders,
            'Content-Type': 'application/json',
        };
        const jwt = this.sessionRepository.getJwt();

        if (jwt !== undefined) {
            headers['session-token'] = jwt;
        }

        const response = await fetch(`${baseUrl}${path}`, {
            body: JSON.stringify(body),
            headers,
            method: 'POST',
        });

        if (response.ok) {
            return await response.json();
        } else if (response.status === 400) {
            const errorResponse = await response.json();

            throw new ValidationError(errorResponse.errorMessageCode);
        } else if (response.status === 403) {
            const errorResponse = await response.json();

            throw new ForbiddenError(errorResponse.errorMessageCode);
        } else {
            throw new Error(response.statusText);
        }
    }
}

export class ValidationError extends Error{
    errorMessageCode: string;
    constructor(errorMessageCode: string) {
        super();
        this.name = 'ValidationError';
        this.errorMessageCode = errorMessageCode;
    }
}

export class ValidationErrorMultiple extends Error{
    errorMessageCodes: string[];
    constructor(errorMessageCodes: string[]) {
        super();
        this.name = 'ValidationErrorMultiple';
        this.errorMessageCodes = errorMessageCodes;
    }
}

export class ForbiddenError extends Error{
    errorMessageCode: string;
    constructor(errorMessageCode: string) {
        super();
        this.name = 'ForbiddenError';
        this.errorMessageCode = errorMessageCode;
    }
}

const baseUrl = Capacitor.getPlatform() !== 'web' ? 'https://app.moticheck.com/api' : '/api';
